import { createAction, createReducer } from '@reduxjs/toolkit';
import {
  DEFAULT_UI_FONT_ID,
  ROBOTO_UI_FONT_ID,
  type UiFontId,
} from '@common/styles/fonts';
import { createSelector } from 'reselect';
import {
  FETCH_APPLICATION_CONFIGS_SUCCESS,
  SHOW_OR_HIDE_APPBAR_MENU,
} from './actions';
import { isPlainObject, isString } from 'lodash';

export const BOOT = createAction<Record<string, any>>(
  'boot saga: sequence all the stuff needed to start the app'
);
export const BOOT_FINISHED = createAction('boot saga finished');

export interface AppBarState {
  isBooting: boolean;
  bootDidFinish: boolean;
  isAppBarMenuOpen: boolean;
  isApplicationConfigsLoaded: boolean;
  applicationConfigs?: {
    configType: string;
    configKey: string;
    value: string | null;
    valueJSON: Record<string, unknown> | null;
    defaultValue: string | null;
    defaultValueJSON: Record<string, unknown> | null;
  }[];
}

const defaultState: AppBarState = {
  isBooting: false,
  bootDidFinish: false,
  isAppBarMenuOpen: false,
  isApplicationConfigsLoaded: false,
};

const reducer = createReducer(defaultState, (builder) => {
  builder
    .addCase(BOOT, (state) => ({
      ...state,
      isBooting: true,
      bootDidFinish: false,
    }))
    .addCase(BOOT_FINISHED, (state) => ({
      ...state,
      isBooting: false,
      bootDidFinish: true,
    }))
    .addCase(SHOW_OR_HIDE_APPBAR_MENU, (state) => ({
      ...state,
      isAppBarMenuOpen: !state.isAppBarMenuOpen,
    }))
    .addCase(FETCH_APPLICATION_CONFIGS_SUCCESS, (state, action) => {
      const { records, count } = action.payload.applicationConfig;
      // The query asks for 100 (services.ts) against a platform default of
      // 30. That is a ceiling, not paging, so it WILL truncate again — and
      // the last time it did, nobody noticed for as long as it took someone
      // to wonder why a registered config had no effect. A missing record and
      // an unregistered definition look identical from in here, so say it out
      // loud when the server reports more than it sent.
      if (typeof count === 'number' && count > records.length) {
        console.warn(
          `[app] application configs truncated: server has ${count}, got ` +
            `${records.length}. Configs past the limit are invisible to the ` +
            `app and every reader falls through to its default. Raise the ` +
            `limit in state/modules/app/services.ts, or page it properly.`
        );
      }
      return {
        ...state,
        applicationConfigs: records,
        isApplicationConfigsLoaded: true,
      };
    });
});

export default reducer;
export const namespace = 'app';

export const boot = (options: Record<string, any> = {}) => BOOT(options);

export const bootFinished = () => BOOT_FINISHED();

const local = (state: any) => state[namespace] as AppBarState;

export const isBooting = (state: any) => local(state)?.isBooting;
export const bootDidFinish = (state: any) => local(state)?.bootDidFinish;
export const selectIsAppBarMenuOpen = (state: any) =>
  local(state)?.isAppBarMenuOpen;
export const selectIsApplicationConfigsLoaded = (state: any) =>
  local(state)?.isApplicationConfigsLoaded;
export const selectRawApplicationConfigs = (state: any) =>
  local(state)?.applicationConfigs;
export const selectApplicationConfigs = createSelector(
  selectRawApplicationConfigs,
  (rac) =>
    rac?.reduce<Record<string, unknown>>((acc, config) => {
      const value = appConfigValue(config);
      if (!value) {
        return acc;
      }
      return {
        ...acc,
        [config.configKey]: value,
      };
    }, {}) || {}
);

/**
 * The organization's UI font.
 *
 * Driven by `useRobotoFont`, a Boolean kill-switch rather than a font name:
 * the app's font is DEFAULT_UI_FONT_ID and this only says "put me back on the
 * pre-VE-32221 Roboto".
 *
 * `selectApplicationConfigs` drops falsy values, so it cannot tell `false`
 * from absent — and here it does not need to, because they are the same
 * answer. That is the whole point of the Boolean shape: absent, `false` and
 * an unregistered definition all mean the default, so nothing has to be
 * registered for the app to be correct. (Contrast the kill-switches in
 * `user-permissions/selectors.ts`, which read raw records via
 * `booleanAppConfig` precisely because for them the two differ.)
 */
export const selectUiFontId = createSelector(
  selectApplicationConfigs,
  (configs): UiFontId =>
    configs.useRobotoFont === true ? ROBOTO_UI_FONT_ID : DEFAULT_UI_FONT_ID
);

export function appConfigValue(
  config: NonNullable<ReturnType<typeof selectRawApplicationConfigs>>[0]
) {
  if (config.configType === 'JSON') {
    // Work around SDK incorrect usage of value rather than valueJSON for JSON config keys
    return getBrokenJSONAppConfigValue(config);
  } else if (config.configType === 'Boolean') {
    // for some reason value / defaultValue are saved as string !!!
    return (config.value || config.defaultValue) === 'true';
  } else if (config.configType === 'Integer') {
    // for some reason value / defaultValue are saved as string !!!
    return config.value !== null
      ? parseInt(config.value)
      : config.defaultValue !== null
        ? parseInt(config.defaultValue)
        : null;
  } else if (config.configType === 'Float') {
    // for some reason value / defaultValue are saved as string !!!
    return config.value !== null
      ? Number(config.value)
      : config.defaultValue !== null
        ? Number(config.defaultValue)
        : null;
  }
  // TODO need to add conversion for Date still
  else {
    return config.value || config.defaultValue;
  }
}

function getBrokenJSONAppConfigValue(
  appConfig: NonNullable<ReturnType<typeof selectRawApplicationConfigs>>[0]
) {
  // Default to valueJSON if it exists
  const valueJSON = appConfig.valueJSON;
  if (valueJSON && Object.keys(valueJSON).length > 0) {
    // for now do not count if it is an empty object
    return valueJSON;
  }

  // next check if there is "value" to handle platform bug - eventually we can get rid of this
  let rawValue = appConfig.value;

  // next check for defaultValueJSON
  if (
    !rawValue &&
    appConfig.defaultValueJSON &&
    Object.keys(appConfig.defaultValueJSON).length > 0
  ) {
    return appConfig.defaultValueJSON;
  }

  // as last resort check the defaultValue
  if (!rawValue) {
    rawValue = appConfig.defaultValue;
  }

  // try to parse value as JSON to handle platform UI deficiency
  if (isString(rawValue)) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const value = JSON.parse(rawValue);
      if (isPlainObject(value)) {
        // Relatively safe cast...
        return value as Record<string, any>;
      } else {
        throw new Error('Invalid JSON structure in app config');
      }
    } catch (_e) {
      console.error('Invalid JSON string in app config:', rawValue);
      return undefined;
    }
  }
}
